/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { atom } from 'jotai'

import { isMultiModuleManual } from '@/components/dashboard/manuals/types'
import { manualAtom } from '@/store/manual/index'
import { findExpandableContentHeadersInActivityContent } from '@/utils/kit-manual-helpers/findExpandableContentHeaders'

/** Type for each link in our navigation. */
type ManualPageLink = {
  /** The label to display for the link. */
  label: string

  /** The URL to navigate to. */
  url: string

  /** The type of page this link represents. */
  type: 'kit' | 'module' | 'getting-started' | 'activity' | 'misc-page' | 'activity-subheader'
}

/**
 * Atom that extracts all pages in order from the manual. This is used to enable navigation between pages.
 */
const manualPagesWithOrderAtom = atom((get) => {
  const manual = get(manualAtom)

  if (!manual) {
    return []
  }

  const pages: Array<ManualPageLink> = []
  const kitSlug = manual.kit.slug

  // Add kit root page
  pages.push({
    label: manual.kit.content.name || 'Kit Home',
    url: `/dashboard/manuals/${kitSlug}`,
    type: 'kit',
  })

  // If we have a multi-module manual, we need to process modules and activities
  if (isMultiModuleManual(manual)) {
    if (manual.getting_started) {
      pages.push({
        label: manual.getting_started.name,
        url: `/dashboard/manuals/${kitSlug}/${manual.getting_started.slug}`,
        type: 'getting-started',
      })
    }

    // Add root-level misc pages
    if (manual.misc_pages && manual.misc_pages.length > 0) {
      manual.misc_pages
        .sort((a, b) => (Number(a.content.order) > Number(b.content.order) ? 1 : -1)) // Assuming order field exists on misc_page content
        .forEach((miscPage) => {
          pages.push({
            label: miscPage.name,
            url: `/dashboard/manuals/${kitSlug}/${miscPage.slug}`,
            type: 'misc-page',
          })
        })
    }

    // Sort modules by order
    const sortedModules = [...manual.modules].sort((a, b) => {
      const orderA = parseInt(a.module.content.order || '0', 10)
      const orderB = parseInt(b.module.content.order || '0', 10)
      return orderA - orderB
    })

    // Process each module and its activities
    sortedModules.forEach((moduleData) => {
      const moduleSlug = moduleData.module.slug

      // Add module page
      pages.push({
        label: moduleData.module.content.name || 'Module',
        url: `/dashboard/manuals/${kitSlug}/${moduleSlug}`,
        type: 'module',
      })

      // Add module-level getting started page if it exists
      if (moduleData.getting_started) {
        pages.push({
          label: moduleData.getting_started.name,
          url: `/dashboard/manuals/${kitSlug}/${moduleSlug}/${moduleData.getting_started.slug}`,
          type: 'getting-started',
        })
      }

      // Add module-level misc pages
      if (moduleData.misc_pages && moduleData.misc_pages.length > 0) {
        moduleData.misc_pages
          .sort((a, b) => (Number(a.content.order) > Number(b.content.order) ? 1 : -1))
          .forEach((miscPage) => {
            pages.push({
              label: miscPage.name,
              url: `/dashboard/manuals/${kitSlug}/${moduleSlug}/${miscPage.slug}`,
              type: 'misc-page',
            })
          })
      }

      // Sort activities by order
      const sortedActivities = [...moduleData.activities].sort((a, b) => {
        const orderA = parseInt(a.activity.content.order || '0', 10)
        const orderB = parseInt(b.activity.content.order || '0', 10)
        return orderA - orderB
      })

      // Add activity pages
      sortedActivities.forEach((activityData) => {
        pages.push({
          label: activityData.activity.content.name || 'Activity',
          url: `/dashboard/manuals/${kitSlug}/${moduleSlug}/${activityData.activity.slug}`,
          type: 'activity',
        })

        const activitySectionHeaders = findExpandableContentHeadersInActivityContent(activityData.activity)
        activitySectionHeaders.forEach((header) => {
          pages.push({
            label: header.title,
            url: `/dashboard/manuals/${kitSlug}/${moduleSlug}/${activityData.activity.slug}#${header._uid}`,
            type: 'activity-subheader',
          })
        })
      })
    })
  } else {
    // Add getting started page if it exists for flat manuals
    if (manual.getting_started) {
      pages.push({
        label: manual.getting_started.name,
        url: `/dashboard/manuals/${kitSlug}/${manual.getting_started.slug}`,
        type: 'getting-started',
      })
    }

    // Add root-level misc pages for flat manuals
    if (manual.misc_pages && manual.misc_pages.length > 0) {
      manual.misc_pages
        .sort((a, b) => (Number(a.content.order) > Number(b.content.order) ? 1 : -1))
        .forEach((miscPage) => {
          pages.push({
            label: miscPage.name,
            url: `/dashboard/manuals/${kitSlug}/${miscPage.slug}`,
            type: 'misc-page',
          })
        })
    }

    // Sort activities by order
    const sortedActivities = [...manual.activities].sort((a, b) => {
      const orderA = parseInt(a.content.order || '0', 10)
      const orderB = parseInt(b.content.order || '0', 10)
      return orderA - orderB
    })

    // Add activity pages
    sortedActivities.forEach((activity) => {
      pages.push({
        label: activity.content.name || 'Activity',
        url: `/dashboard/manuals/${kitSlug}/${activity.slug}`,
        type: 'activity',
      })

      const activitySectionHeaders = findExpandableContentHeadersInActivityContent(activity)
      activitySectionHeaders.forEach((header) => {
        pages.push({
          label: header.title,
          url: `/dashboard/manuals/${kitSlug}/${activity.slug}#${header._uid}`,
          type: 'activity-subheader',
        })
      })
    })
  }

  return pages
})
manualPagesWithOrderAtom.debugLabel = 'manualPagesWithOrderAtom'

export { manualPagesWithOrderAtom }
export type { ManualPageLink }
